import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  padSnapshotsToMinimum,
  parseSnapshotFolderDate,
  reviveMaps,
  sortSnapshotsByDate,
  type PriceSnapshot,
  type SnapshotData,
} from '../domain/pricing';
import { fetchStorageJson, listStorageFolders } from '../services/firebaseStorage';

async function fetchSnapshot(folderName: string, make: string, model: string): Promise<PriceSnapshot | null> {
  try {
    const data = await fetchStorageJson<SnapshotData>(
      `prices/${folderName}/${make}/${model}/filtered_data_v2.json`,
      reviveMaps,
    );
    return { date: parseSnapshotFolderDate(folderName), data, folderName };
  } catch (error) {
    // A single missing/malformed snapshot shouldn't take down the whole chart - the scraper
    // doesn't produce a file for every make/model in every snapshot run, so this is expected.
    console.warn(`Skipping price snapshot "${folderName}" for ${make} ${model} (missing or unreadable):`, error);
    return null;
  }
}

/** Historical average-price snapshots for a make/model, sorted ascending and padded to a minimum chart length. */
export function useHistoricalPrices(make: string, model: string): UseQueryResult<PriceSnapshot[]> {
  return useQuery({
    queryKey: ['historicalPrices', make, model],
    queryFn: async () => {
      const folders = await listStorageFolders('prices/');
      const snapshots = await Promise.all(folders.map((folder) => fetchSnapshot(folder, make, model)));
      const loaded = snapshots.filter((snapshot): snapshot is PriceSnapshot => snapshot !== null);
      return padSnapshotsToMinimum(sortSnapshotsByDate(loaded));
    },
    enabled: Boolean(make && model),
  });
}
